/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { Link, useParams } from "react-router-dom";
import { useThemeState } from "../../hooks/useThemeState";
import { useInventoryState } from "../../hooks/useInventoryState";
import { useStorefrontSettingsState } from "../../hooks/useStorefrontSettingsState";
import { useSessionState } from "../../hooks/useSessionState";
import { useCatalogState } from "../../hooks/useCatalogState";

export const BreadCrumbs = () => {
    const inventoryState = useInventoryState();
    const settingsState = useStorefrontSettingsState();
    const isLoadingSettings = settingsState.get().isLoading;
    const settings = settingsState.get().settings;
    const sessionState = useSessionState();
    const loggedIn = sessionState.get().loggedIn;
    const themeState = useThemeState();
    const theme = themeState.getTheme();
    const catalogState = useCatalogState();
    
    // Get URL parameters for current state (these are route parameters, not query parameters)
    const { catalogid, departmentid, categoryid, subcategoryid } = useParams();
    
    // Get catalog, department, category, and subcategory from state based on URL params
    const catalog = catalogState.get().catalogs?.find(c => c.Id === catalogid);
    const department = catalog && departmentid ? 
        inventoryState.get().productCategoriesTree?.Departments?.find(d => d.Id === departmentid) : undefined;
    const category = department && categoryid ? 
        department.Categories?.find(c => c.Id === categoryid) : undefined;
    const subCategory = category && subcategoryid ? 
        category.SubCategories?.find(s => s.Id === subcategoryid) : undefined;

    // console.log('🍞 BreadCrumbs state:', {
    //     catalogid,
    //     departmentid,
    //     categoryid,
    //     subcategoryid,
    //     catalog: catalog?.Name,
    //     department: department?.Name,
    //     category: category?.Name,
    //     subCategory: subCategory?.Name,
    //     catalogsCount: catalogState.get().catalogs?.length,
    //     departmentsCount: inventoryState.get().productCategoriesTree?.Departments?.length,
    //     settings: !!settings,
    //     isLoadingSettings,
    //     loggedIn
    // });

    if (!settings || isLoadingSettings || (settings && !settings.IsAnonymousAccessEnabled && !loggedIn)) {
        // console.log('🍞 BreadCrumbs returning null due to settings/auth check');
        return null;
    }

    // Build URLs for navigation (using route parameters, not query parameters)
    const catalogUrl = catalog ? `/inventory/${catalog.Id}` : '';
    const departmentUrl = catalog && department ? `/inventory/${catalog.Id}/${department.Id}` : '';
    const categoryUrl = catalog && department && category ? 
        `/inventory/${catalog.Id}/${department.Id}/${category.Id}` : '';

    // console.log('🍞 BreadCrumbs rendering with:', {
    //     catalogUrl,
    //     departmentUrl,
    //     categoryUrl,
    //     hasCatalog: !!catalog,
    //     hasDepartment: !!department,
    //     hasCategory: !!category,
    //     hasSubCategory: !!subCategory
    // });

    return (<div css={css`flex:1 1 0;display:flex;flex-wrap:wrap;padding:0 1em;font-weight:700;align-items:center;`}>
        {/* Show catalog name */}
        {catalog && (
            <>
                {!department ? (
                    <div css={css`color:${theme.palette.primary.main};`}>{catalog.Name}</div>
                ) : (
                    <Link
                        to={catalogUrl}
                        css={css`
                            color:${theme.palette.primary.main};
                            text-decoration:none;
                            &:hover {
                                text-decoration:underline;
                            }`}
                        onClick={() => {
                            // Clear search text when breadcrumb is clicked since they're mutually exclusive
                            inventoryState.clearSearchTextAsync();
                        }}>
                        {catalog.Name}
                    </Link>
                )}
            </>
        )}

        {/* Show department */}
        {department && (
            <>
                <div css={css`flex:0 0 auto;margin:0 .5em;color:${theme.palette.siteTextPrimary};`}>/</div>
                {!category ? (
                    <div css={css`color:${theme.palette.primary.main};`}>{department.Name}</div>
                ) : (
                    <Link
                        to={departmentUrl}
                        css={css`
                            color:${theme.palette.primary.main};
                            text-decoration:none;
                            &:hover {
                                text-decoration:underline;
                            }`}
                        onClick={() => {
                            // Clear search text when breadcrumb is clicked since they're mutually exclusive
                            inventoryState.clearSearchTextAsync();
                        }}>
                        {department.Name}
                    </Link>
                )}
            </>
        )}

        {/* Show category */}
        {category && (
            <>
                <div css={css`flex:0 0 auto;margin:0 .5em;color:${theme.palette.siteTextPrimary};`}>/</div>
                {!subCategory ? (
                    <div css={css`color:${theme.palette.primary.main};`}>{category.Name}</div>
                ) : (
                    <Link
                        to={categoryUrl}
                        css={css`
                            color:${theme.palette.primary.main};
                            text-decoration:none;
                            &:hover {
                                text-decoration:underline;
                            }`}
                        onClick={() => {
                            // Clear search text when breadcrumb is clicked since they're mutually exclusive
                            inventoryState.clearSearchTextAsync();
                        }}>
                        {category.Name}
                    </Link>
                )}
            </>
        )}

        {/* Show subcategory */}
        {subCategory && (
            <>
                <div css={css`flex:0 0 auto;margin:0 .5em;color:${theme.palette.siteTextPrimary};`}>/</div>
                <div css={css`color:${theme.palette.primary.main};`}>{subCategory.Name}</div>
            </>
        )}
        
        {/* Fallback to show if component is rendering but no data */}
        {!catalog && !department && !category && !subCategory && (
            <div css={css`color:${theme.palette.siteTextPrimary};font-style:italic;`}>
                Breadcrumbs: No data found (catalogid: {catalogid || 'none'})
            </div>
        )}
    </div>);
}